/**
 * The gift page's behaviour (2026-10-20 founder). The markup is gift.html; this
 * wires it up inside the page root: the buy sheet, the quotes rail, the sample
 * Ember Book, the note writer (POST /api/gift/note) and Little Ember in 3D
 * (three.js, loaded when the section comes near). Returns a cleanup.
 */
import mascotSvg from './mascot.svg?raw';
import { trackFailed } from '../../../../shared-web/track';

type Cleanup = () => void;

export function mountGift(root: HTMLElement): Cleanup {
  const q = <T extends Element = HTMLElement>(sel: string) => root.querySelector(sel) as T | null;
  const qa = <T extends Element = HTMLElement>(sel: string) => Array.from(root.querySelectorAll(sel)) as T[];
  const cleanups: Cleanup[] = [];
  const on = (el: EventTarget | null, type: string, fn: EventListener, opts?: AddEventListenerOptions) => {
    if (!el) return;
    el.addEventListener(type, fn, opts);
    cleanups.push(() => el.removeEventListener(type, fn, opts));
  };

  // Ember's mascot is drawn once and placed wherever the markup asks for it.
  qa('[data-mascot]').forEach((el) => { el.innerHTML = mascotSvg; });

  mountSheets(root, q, qa, on);
  mountRail(q, on);
  cleanups.push(mountBook(root, q, on));
  mountWriter(q, on);
  cleanups.push(mountLittle(root, q));
  return () => cleanups.forEach((fn) => fn());
}

type Q = <T extends Element = HTMLElement>(sel: string) => T | null;
type QA = <T extends Element = HTMLElement>(sel: string) => T[];
type On = (el: EventTarget | null, type: string, fn: EventListener, opts?: AddEventListenerOptions) => void;

// ---------- the buy sheet ----------
const PLANS: Record<string, [string, number]> = {
  month: ['1 Month of Ember', 9.99], season: ['3 Months of Ember', 25], year: ['1 Year of Ember', 100],
  full: ['The Ember Year', 230], book1: ['1 Ember Book', 49], books: ['3 Ember Books', 129],
};
const DELIVERY_HELP: Record<string, string> = {
  link: "You'll get a gift link to text them yourself, so it comes from you.",
  date: 'We email them on the day you pick, with your note.',
  card: 'Print a card with a code, wrap it, hand it over.',
};

function mountSheets(root: HTMLElement, q: Q, qa: QA, on: On) {
  const scrim = q('#buy');
  const sel = q<HTMLSelectElement>('#plan');
  const closeAll = () => qa('.scrim').forEach((s) => { s.hidden = true; });
  qa('[data-close]').forEach((b) => on(b, 'click', closeAll));
  qa('.scrim').forEach((s) => on(s, 'click', (e) => { if (e.target === s) closeAll(); }));
  on(root.ownerDocument, 'keydown', (e) => { if ((e as KeyboardEvent).key === 'Escape') closeAll(); });
  const paint = () => {
    if (!sel) return;
    const [name, price] = PLANS[sel.value] || PLANS.year;
    const s = `$${price.toFixed(2)}`;
    const set = (id: string, v: string) => { const el = q(id); if (el) el.textContent = v; };
    set('#sumname', name); set('#sumprice', s); set('#sumtot', s);
  };
  on(sel, 'change', paint);
  qa<HTMLElement>('[data-buy]').forEach((b) => on(b, 'click', () => {
    if (sel) sel.value = b.dataset.buy || 'year';
    paint();
    if (scrim) scrim.hidden = false;
    q<HTMLInputElement>('#to')?.focus();
  }));
  qa<HTMLInputElement>('input[name="dl"]').forEach((r) => on(r, 'change', () => {
    const help = q('#dlhelp');
    if (r.checked && help) help.textContent = DELIVERY_HELP[r.value] || '';
  }));
  on(q('#buyform'), 'submit', (e) => e.preventDefault());
}

// ---------- the quotes rail ----------
function mountRail(q: Q, on: On) {
  const rail = q('#rail');
  if (!rail) return;
  const step = () => (rail.querySelector('.quote')?.getBoundingClientRect().width || 320) + 20;
  on(q('#prev'), 'click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
  on(q('#next'), 'click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
}

// ---------- the sample Ember Book ----------
function mountBook(root: HTMLElement, q: Q, on: On): Cleanup {
  const tpl = q<HTMLTemplateElement>('#bookpages');
  const wrap = q('#bookwrap'), stage = q('#bookstage'), book = q('#bookel');
  if (!tpl || !wrap || !stage || !book) return () => {};
  const all = Array.from(tpl.content.children).map((el) => ({ kind: (el as HTMLElement).dataset.kind || '', html: el.innerHTML }));
  const PW = 440, PH = 620;
  let mode = '', leaves: HTMLElement[] = [], current = 0, busy = false;
  const faceClass = (p?: { kind: string }) => p ? (p.kind === 'cover' ? 'cover' : p.kind === 'backcover' ? 'cover backc' : p.kind === 'endpaper' ? 'endpaper' : '') : 'blank';
  const label = () => {
    const where = q('#bwhere'), n = leaves.length;
    if (where) where.textContent = current === 0 ? 'Cover' : current === n ? 'Back cover'
      : mode === 'spread' ? `Pages ${current * 2 - 1}–${current * 2} of ${n * 2 - 2}` : `Page ${current} of ${n - 2}`;
    const prev = q<HTMLButtonElement>('#bprev'), next = q<HTMLButtonElement>('#bnext');
    if (prev) prev.disabled = current === 0;
    if (next) next.disabled = current === n;
  };
  const layout = (animate = true, moving = -1) => {
    leaves.forEach((leaf, i) => {
      const flipped = i < current;
      leaf.style.transition = animate ? '' : 'none';
      leaf.classList.toggle('flipped', flipped);
      leaf.style.zIndex = String(i === moving ? 1000 : flipped ? i + 1 : leaves.length - i);
      if (mode === 'single') leaf.style.visibility = flipped && i !== moving ? 'hidden' : 'visible';
    });
    book.style.transition = animate ? '' : 'none';
    book.style.transform = mode === 'spread' ? (current === 0 ? 'translateX(-25%)' : current === leaves.length ? 'translateX(25%)' : 'none') : 'none';
    if (!animate) requestAnimationFrame(() => { leaves.forEach((l) => { l.style.transition = ''; }); book.style.transition = ''; });
    label();
  };
  const size = () => {
    const avail = wrap.clientWidth;
    const want = avail < 760 ? 'single' : 'spread';
    if (want !== mode) { build(want); return; }
    const w = mode === 'spread' ? PW * 2 : PW;
    const s = Math.min(1, avail / w);
    stage.style.width = `${w}px`; stage.style.height = `${PH}px`;
    stage.style.transform = `scale(${s})`;
    wrap.style.height = `${PH * s}px`;
  };
  function build(next: string) {
    const progress = leaves.length ? current / leaves.length : 0;
    mode = next; leaves = []; book!.innerHTML = '';
    stage!.classList.toggle('single', mode === 'single');
    const list = mode === 'spread'
      ? Array.from({ length: Math.ceil(all.length / 2) }, (_, i) => [all[2 * i], all[2 * i + 1]])
      : all.filter((p) => p.kind !== 'endpaper').map((p) => [p, undefined]);
    list.forEach(([f, b]) => {
      const leaf = root.ownerDocument.createElement('div');
      leaf.className = 'leaf';
      leaf.innerHTML = `<div class="face front ${faceClass(f)}">${f!.html}</div><div class="face back ${faceClass(b)}">${b ? b.html : ''}</div>`;
      book!.appendChild(leaf); leaves.push(leaf);
    });
    current = Math.round(progress * leaves.length);
    layout(false); size();
  }
  const go = (dir: number) => {
    if (busy) return;
    const target = current + dir;
    if (target < 0 || target > leaves.length) return;
    busy = true;
    const moving = dir > 0 ? current : current - 1;
    leaves[moving].style.visibility = 'visible';
    leaves[moving].style.zIndex = '1000';
    requestAnimationFrame(() => {
      current = target; layout(true, moving);
      setTimeout(() => { busy = false; layout(false); }, 1000);
    });
  };
  on(book, 'click', (e) => {
    const r = book.getBoundingClientRect(), x = (e as MouseEvent).clientX - r.left;
    go(mode === 'single' ? (x > r.width * 0.3 ? 1 : -1) : (x > r.width / 2 ? 1 : -1));
  });
  let sx: number | null = null;
  on(book, 'pointerdown', (e) => { sx = (e as PointerEvent).clientX; });
  on(book, 'pointerup', (e) => {
    const x = (e as PointerEvent).clientX;
    if (sx != null && Math.abs(x - sx) > 40) { e.stopPropagation(); go(x < sx ? 1 : -1); }
    sx = null;
  });
  on(book, 'keydown', (e) => { const k = (e as KeyboardEvent).key; if (k === 'ArrowRight') go(1); if (k === 'ArrowLeft') go(-1); });
  on(q('#bprev'), 'click', () => go(-1));
  on(q('#bnext'), 'click', () => go(1));
  const win = root.ownerDocument.defaultView!;
  win.addEventListener('resize', size);
  build(wrap.clientWidth < 760 ? 'single' : 'spread');
  return () => win.removeEventListener('resize', size);
}

// ---------- the note writer ----------
function mountWriter(q: Q, on: On) {
  const form = q<HTMLFormElement>('#wform');
  const out = q('#wout'), note = q('#wnote'), go = q<HTMLButtonElement>('#wgo'), again = q<HTMLButtonElement>('#wagain'), copy = q<HTMLButtonElement>('#wcopy');
  if (!form || !out || !note || !go || !again || !copy) return;
  on(copy, 'click', async () => {
    try { await navigator.clipboard.writeText(out.textContent || ''); copy.textContent = 'Copied'; setTimeout(() => { copy.textContent = 'Copy'; }, 1600); }
    catch { const r = document.createRange(); r.selectNodeContents(out); const s = getSelection(); s?.removeAllRanges(); s?.addRange(r); }
  });
  let version = 0;
  const val = (id: string) => (q<HTMLInputElement>(id)?.value || '').trim();
  const write = async () => {
    version += 1;
    go.disabled = true; again.hidden = true;
    out.classList.add('wait'); out.textContent = 'Writing something warm…'; note.textContent = '';
    let reason = '';
    try {
      const res = await fetch('/api/gift/note', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ relationship: val('#wrel'), name: val('#wname'), lately: val('#wnow'), why: val('#wwhy'), version }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && typeof data.note === 'string' && data.note.trim()) {
        out.textContent = data.note.trim();
        note.textContent = 'Change anything you like. It’s your note.';
      } else {
        reason = String(data.error || `http_${res.status}`);
      }
    } catch (err) {
      reason = `network_error:${String((err as Error)?.message || err).slice(0, 80)}`;
    }
    out.classList.remove('wait');
    if (reason) {
      trackFailed('gift_note', reason);
      out.textContent = '';
      note.textContent = reason === 'rate_limited' ? 'That’s a lot of notes for now. Try again a little later.' : 'That one didn’t come through. Try again.';
    }
    again.hidden = false; go.disabled = false;
  };
  on(form, 'submit', (e) => { e.preventDefault(); void write(); });
  on(again, 'click', () => { void write(); });
}

// ---------- Little Ember in 3D ----------
function mountLittle(root: HTMLElement, q: Q): Cleanup {
  const canvas = q<HTMLCanvasElement>('#doll3d'), section = q('#little'), fallback = q('#dollfallback');
  if (!canvas || !section || !fallback) return () => {};
  let dispose: Cleanup = () => {};
  let started = false;
  const start = () => {
    if (started) return;
    started = true;
    import('./little-3d').then(({ startLittle }) => {
      dispose = startLittle({ root, canvas, section, q });
    }).catch(() => { canvas.hidden = true; fallback.hidden = false; });
  };
  const win = root.ownerDocument.defaultView!;
  if ('IntersectionObserver' in win) {
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { io.disconnect(); start(); } }, { rootMargin: '600px 0px' });
    io.observe(section);
    return () => { io.disconnect(); dispose(); };
  }
  start();
  return () => dispose();
}
